import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Circle, PlayCircle, FileText, HelpCircle, Lock } from 'lucide-react';
import { ProgressBar } from './HcCourseBits';

const TYPE_ICON = { VIDEO: PlayCircle, ARTICLE: FileText, QUIZ: HelpCircle };

const LessonRow = ({ lesson, index, active, onOpen }) => {
  const Icon = TYPE_ICON[lesson.type] || FileText;
  return (
    <button onClick={() => !lesson.locked && onOpen(lesson.id)} disabled={lesson.locked} data-testid={`hc-lesson-item-${lesson.id}`}
      title={lesson.locked ? 'Complete the previous step to unlock' : undefined}
      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? 'bg-[#F6EAF5]' : lesson.locked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
      {lesson.completed
        ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-testid={`hc-lesson-done-${lesson.id}`} />
        : lesson.locked ? <Lock className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-testid={`hc-lesson-locked-${lesson.id}`} />
        : active ? <Circle className="w-5 h-5 text-[#922A8C] fill-[#922A8C]/15 shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />}
      <span className={`text-sm flex-1 ${active ? 'font-semibold text-[#363636]' : 'text-gray-600'}`}>{index}. {lesson.title}</span>
      <Icon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
    </button>
  );
};

const Section = ({ section, open, onToggle, activeId, onOpen, startIndex }) => (
  <div className="border border-gray-100 rounded-2xl" data-testid={`hc-section-${section.id}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3.5 text-left" data-testid={`hc-section-toggle-${section.id}`}>
      <span className="font-bold text-[#363636]">{section.title}</span>
      <span className="flex items-center gap-2 text-xs text-gray-500">
        {section.lessons.filter((l) => l.completed).length}/{section.lessons.length}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </span>
    </button>
    {open && (
      <div className="px-2 pb-2 space-y-0.5">
        {section.lessons.map((l, i) => <LessonRow key={l.id} lesson={l} index={startIndex + i} active={l.id === activeId} onOpen={onOpen} />)}
      </div>
    )}
  </div>
);

export const CourseOutline = ({ course, activeId, onOpen }) => {
  const activeSection = course.sections.find((s) => s.lessons.some((l) => l.id === activeId))?.id || course.sections[0]?.id;
  const [openIds, setOpenIds] = useState(new Set([activeSection]));
  useEffect(() => { if (activeSection) setOpenIds((p) => new Set([...p, activeSection])); }, [activeSection]);
  const toggle = (id) => setOpenIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  let counter = 1;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="hc-course-outline">
      <h2 className="text-2xl font-bold text-[#363636]">Course Content</h2>
      <div className="flex items-center justify-between text-sm text-gray-500 mt-1 mb-2">
        <span data-testid="hc-outline-count">{course.completed_lessons} of {course.total_lessons} steps completed</span>
        <span className="text-xl font-extrabold text-[#363636]" data-testid="hc-outline-progress">{course.progress}%</span>
      </div>
      <ProgressBar value={course.progress} />
      <div className="space-y-3 mt-5 max-h-[65vh] overflow-y-auto pr-1">
        {course.sections.map((s) => {
          const start = counter;
          counter += s.lessons.length;
          return <Section key={s.id} section={s} open={openIds.has(s.id)} onToggle={() => toggle(s.id)} activeId={activeId} onOpen={onOpen} startIndex={start} />;
        })}
      </div>
    </div>
  );
};
